/**
 * Client-side behavioural capture.
 *
 * What this deliberately does NOT do: read clipboard contents, read keystrokes, or
 * touch anything outside the test page. Paste events record only the LENGTH of what
 * landed in the field. The candidate agreed to exactly this list on the monitoring
 * page, and this file should not grow beyond it without that text changing too.
 */

export type ClientEvent = {
  type: string;
  ts: number;
  sectionId?: string;
  itemId?: string;
  durationMs?: number;
  resultLength?: number;
  charsTyped?: number;
  finalLength?: number;
  burstCharsPerSecond?: number;
  idleMs?: number;
  width?: number;
  height?: number;
  previousWidth?: number;
  previousHeight?: number;
  /** Magnitude of a resize jump, in pixels. */
  delta?: number;
  /** Mouse-activity aggregates. */
  moveCount?: number;
  distancePx?: number;
  maxSpeed?: number;
  keyEvents?: number;
  note?: string;
};

type Context = { sectionId?: string; itemId?: string };

const FLUSH_INTERVAL_MS = 5_000;
const MAX_QUEUE = 300;

export class TelemetryCollector {
  private queue: ClientEvent[] = [];
  private context: Context = {};
  private blurredAt: number | null = null;
  private hiddenAt: number | null = null;
  private lastSize = { width: 0, height: 0 };
  private flushTimer: ReturnType<typeof setInterval> | null = null;
  private detachers: Array<() => void> = [];
  private devtoolsReported = false;

  // Mouse-movement accumulation, flushed periodically as a humanness signal. Only
  // aggregates leave the browser — counts and distance, never a cursor trail.
  private mouse = { moves: 0, distancePx: 0, maxSpeed: 0, keyEvents: 0, lastX: 0, lastY: 0, lastT: 0, seen: false };
  private mouseTimer: ReturnType<typeof setInterval> | null = null;

  constructor(private readonly token: string) {}

  setContext(context: Context): void {
    this.context = context;
  }

  push(event: Omit<ClientEvent, 'ts'> & { ts?: number }): void {
    if (this.queue.length >= MAX_QUEUE) return;
    this.queue.push({
      ...this.context,
      ...event,
      ts: event.ts ?? Date.now(),
    } as ClientEvent);
  }

  start(): void {
    if (typeof window === 'undefined') return;

    this.lastSize = { width: window.innerWidth, height: window.innerHeight };

    const onBlur = () => {
      this.blurredAt = Date.now();
    };
    const onFocus = () => {
      if (this.blurredAt !== null) {
        this.push({ type: 'focus_loss', durationMs: Date.now() - this.blurredAt, ts: this.blurredAt });
        this.push({ type: 'focus_return' });
        this.blurredAt = null;
      }
    };

    const onVisibility = () => {
      if (document.visibilityState === 'hidden') {
        this.hiddenAt = Date.now();
        this.push({ type: 'visibility_hidden' });
        // Hidden tabs get throttled, so push what we have while we still can.
        this.flush(true);
      } else {
        if (this.hiddenAt !== null) {
          this.push({ type: 'visibility_visible', durationMs: Date.now() - this.hiddenAt });
          this.hiddenAt = null;
        }
      }
    };

    const onCopy = () => this.push({ type: 'copy' });
    const onCut = () => this.push({ type: 'cut' });

    const onPaste = (event: ClipboardEvent) => {
      // Length only. The text itself is never read, stored, or transmitted.
      const length = event.clipboardData?.getData('text')?.length ?? 0;
      this.push({ type: 'paste', resultLength: length });
    };

    const onFullscreenChange = () => {
      this.push({ type: document.fullscreenElement ? 'fullscreen_enter' : 'fullscreen_exit' });
    };

    const onKeyDown = (event: KeyboardEvent) => {
      if (event.key === 'PrintScreen') this.push({ type: 'printscreen' });
    };

    const onResize = () => {
      const width = window.innerWidth;
      const height = window.innerHeight;
      const delta = Math.abs(width - this.lastSize.width) + Math.abs(height - this.lastSize.height);
      // Only abrupt jumps are interesting; a dragged window edge is noise.
      if (delta > 150) {
        this.push({
          type: 'resize_jump',
          width,
          height,
          previousWidth: this.lastSize.width,
          previousHeight: this.lastSize.height,
          delta,
        });
      }
      this.lastSize = { width, height };
      this.checkDevtools();
    };

    // Mouse movement. Sampled cheaply: we accumulate counts, path distance and peak
    // speed, and emit only aggregates. A session with real cursor movement looks
    // human; one with keystrokes but no pointer movement is worth a human glance.
    const onMouseMove = (event: MouseEvent) => {
      const now = Date.now();
      this.mouse.seen = true;
      if (this.mouse.lastT !== 0) {
        const dx = event.clientX - this.mouse.lastX;
        const dy = event.clientY - this.mouse.lastY;
        const dist = Math.sqrt(dx * dx + dy * dy);
        const dt = now - this.mouse.lastT;
        this.mouse.distancePx += dist;
        if (dt > 0) this.mouse.maxSpeed = Math.max(this.mouse.maxSpeed, dist / dt);
      }
      this.mouse.moves += 1;
      this.mouse.lastX = event.clientX;
      this.mouse.lastY = event.clientY;
      this.mouse.lastT = now;
    };
    const onKeyForRatio = () => {
      this.mouse.keyEvents += 1;
    };

    window.addEventListener('blur', onBlur);
    window.addEventListener('focus', onFocus);
    document.addEventListener('visibilitychange', onVisibility);
    document.addEventListener('copy', onCopy);
    document.addEventListener('cut', onCut);
    document.addEventListener('paste', onPaste as EventListener);
    document.addEventListener('fullscreenchange', onFullscreenChange);
    window.addEventListener('keydown', onKeyDown);
    window.addEventListener('keydown', onKeyForRatio);
    window.addEventListener('resize', onResize);
    // passive: never blocks scrolling or interaction.
    window.addEventListener('mousemove', onMouseMove, { passive: true });

    this.detachers = [
      () => window.removeEventListener('blur', onBlur),
      () => window.removeEventListener('focus', onFocus),
      () => document.removeEventListener('visibilitychange', onVisibility),
      () => document.removeEventListener('copy', onCopy),
      () => document.removeEventListener('cut', onCut),
      () => document.removeEventListener('paste', onPaste as EventListener),
      () => document.removeEventListener('fullscreenchange', onFullscreenChange),
      () => window.removeEventListener('keydown', onKeyDown),
      () => window.removeEventListener('keydown', onKeyForRatio),
      () => window.removeEventListener('resize', onResize),
      () => window.removeEventListener('mousemove', onMouseMove),
    ];

    this.flushTimer = setInterval(() => void this.flush(), FLUSH_INTERVAL_MS);
    // Emit a movement summary periodically so the signal covers the whole session,
    // not just its final moments.
    this.mouseTimer = setInterval(() => this.flushMouse(), 15_000);
    this.checkDevtools();
  }

  /** Pushes an aggregate mouse-activity event and resets the accumulator. */
  private flushMouse(): void {
    if (this.mouse.moves === 0 && this.mouse.keyEvents === 0) return;
    this.push({
      type: 'mouse_activity',
      moveCount: this.mouse.moves,
      distancePx: Math.round(this.mouse.distancePx),
      maxSpeed: Math.round(this.mouse.maxSpeed * 1000), // px/s
      keyEvents: this.mouse.keyEvents,
    });
    this.mouse.moves = 0;
    this.mouse.distancePx = 0;
    this.mouse.maxSpeed = 0;
    this.mouse.keyEvents = 0;
  }

  /**
   * Geometry heuristic: a large gap between the outer window and the viewport is
   * consistent with a docked devtools panel. It is also consistent with a browser
   * sidebar, a zoom level, or an unusual OS chrome — which is why the report labels
   * this "suspected" and the reviewer is told it produces false positives.
   */
  private checkDevtools(): void {
    if (this.devtoolsReported) return;
    if (typeof window === 'undefined') return;

    const widthGap = window.outerWidth - window.innerWidth;
    const heightGap = window.outerHeight - window.innerHeight;

    if (widthGap > 220 || heightGap > 260) {
      this.devtoolsReported = true;
      this.push({ type: 'devtools_suspected', width: widthGap, height: heightGap });
    }
  }

  async flush(useBeacon = false): Promise<void> {
    if (this.queue.length === 0) return;
    const events = this.queue.splice(0, this.queue.length);
    const payload = JSON.stringify({ token: this.token, events });

    try {
      if (useBeacon && typeof navigator !== 'undefined' && navigator.sendBeacon) {
        navigator.sendBeacon('/api/session/telemetry', new Blob([payload], { type: 'application/json' }));
        return;
      }
      await fetch('/api/session/telemetry', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: payload,
        keepalive: useBeacon,
      });
    } catch {
      // Telemetry is best-effort: never let a failed batch interrupt the test.
    }
  }

  stop(): void {
    this.flushMouse();
    for (const detach of this.detachers) detach();
    this.detachers = [];
    if (this.flushTimer) clearInterval(this.flushTimer);
    this.flushTimer = null;
    if (this.mouseTimer) clearInterval(this.mouseTimer);
    this.mouseTimer = null;
  }
}

/** Per-item timing, including typing dynamics on free-text answers. */
export class ItemTimer {
  private startedAt = Date.now();
  private firstKeystrokeAt: number | null = null;
  private charsTyped = 0;
  private lastInputAt: number | null = null;
  private idleMs = 0;

  /** A pause longer than this between keystrokes counts as an idle gap. */
  private static readonly IDLE_THRESHOLD_MS = 20_000;

  reset(): void {
    this.startedAt = Date.now();
    this.firstKeystrokeAt = null;
    this.charsTyped = 0;
    this.lastInputAt = null;
    this.idleMs = 0;
  }

  /** Called on every input event. `delta` is the change in field length. */
  registerInput(delta: number): void {
    const now = Date.now();
    if (this.firstKeystrokeAt === null) this.firstKeystrokeAt = now;
    if (this.lastInputAt !== null) {
      const gap = now - this.lastInputAt;
      if (gap > ItemTimer.IDLE_THRESHOLD_MS) this.idleMs += gap;
    }
    this.lastInputAt = now;
    // Counting only positive deltas approximates keystrokes without recording them.
    if (delta > 0) this.charsTyped += delta;
  }

  snapshot(finalLength: number) {
    const now = Date.now();
    const totalMs = now - this.startedAt;
    return {
      firstKeystrokeAt: this.firstKeystrokeAt,
      totalMs,
      idleMs: this.idleMs,
      charsTyped: this.charsTyped,
      finalLength,
      burstCharsPerSecond:
        totalMs > 0 ? Math.round((this.charsTyped / (totalMs / 1000)) * 100) / 100 : 0,
    };
  }
}
